import {
    Approved,
    DecisionDataStore,
    Declined,
    LoanSignContainer,
    SignatureDataStore,
    UnderReview,
} from '@servicetitan/financing-decision-shared';
import { ApplicationWorkflow } from './ApplicationWorkflow';
import { SecondLookApplicationWorkflow } from './SecondLookApplicationWorkflow';
import { DecisionWorkflow } from './DecisionWorkflow';
import { SubmitLoan } from './SubmitLoan';
import {
    WorkflowStage,
    WorkflowStore,
    useProviderConfig,
    SetupDataStore,
} from '@servicetitan/financing-workflow-shared';
import { SubmitApplicationDecision } from '@servicetitan/financing-api-client';
import {
    ApplicationSubmitToDecisionProvider,
    ErrorBoundary,
    reportActiveApplicationSubmitToDecisionOutcome,
} from '@servicetitan/financing-utils-shared';
import { SecondLookSubmitLoanWorkflow } from './SecondLookSubmitLoanWorkflow';
import { SecondLookDecisionWorkflow } from './SecondLookDecisionWorkflow';
import { WorkflowLayoutsStore } from '../layout';
import { ExternalApplicationWorkflowStore } from '../external-application';
import { ResetWorkflow } from './ResetWorkflow';
import { SelectPlanStep, SelectSecondLookStep, CustomerAuthStep } from '../steps';
import { ReviewingWorkflow } from './ReviewingWorkflow';
import { WellsFargoReviewingWorkflow } from './WellsFargoReviewingWorkflow';
import { ApplicationWithCoApplicationWorkflow } from './ApplicationWithCoApplicationWorkflow';
import { TransitionToHardApp } from './TransitionToHardApp';
import { UpdateOfferWorkflow } from './UpdateOfferWorkflow';
import { ReactNode } from 'react';
import { SUBMIT_TO_DECISION_STAGES } from './submitToDecisionStages';

interface WorkflowRendererProps {
    workflowStage: WorkflowStage;
    canChangeProvider?: boolean;
}

const reportSubmitToDecisionError = () => {
    reportActiveApplicationSubmitToDecisionOutcome('error');
};

export const WorkflowRenderer = ({ workflowStage, canChangeProvider }: WorkflowRendererProps) => {
    const { decisionData } = DecisionDataStore.useStore();
    const { signatureData } = SignatureDataStore.useStore();
    const { setWorkflowStage } = WorkflowStore.useStore();
    const { DefaultLayout, FinalStepLayout } = WorkflowLayoutsStore.useStore();
    const { ExternalApplicationWorkflow } = ExternalApplicationWorkflowStore.useStore();
    const { hasMailingAddressStep, useCustomerAuthFlow } = useProviderConfig();
    const { setupData } = SetupDataStore.useStore();
    const onPlanSelected = () => {
        // Soft phase may already have completed customer auth; don't re-challenge after soft→hard.
        if (useCustomerAuthFlow && !setupData?.hasTransitionedToHardApp) {
            setWorkflowStage('customer-auth');
        } else if (!hasMailingAddressStep) {
            // Providers without separate mailing address step use the compact workflow
            // with job details inline (e.g., ServiceFinance with Turns, GoodLeap, GreenSky)
            setWorkflowStage('application-with-co-application');
        } else {
            setWorkflowStage('application');
        }
    };

    let stageContent: ReactNode;
    switch (workflowStage) {
        case 'external-application':
            stageContent = <ExternalApplicationWorkflow />;
            break;
        case 'transition-to-hard-app':
            stageContent = (
                <TransitionToHardApp onCompleted={() => setWorkflowStage('plan-selection')} />
            );
            break;
        case 'plan-selection':
            stageContent = (
                <DefaultLayout>
                    <SelectPlanStep
                        canChangeProvider={canChangeProvider}
                        onPlanSelected={onPlanSelected}
                    />
                </DefaultLayout>
            );
            break;
        case 'customer-auth':
            stageContent = (
                <DefaultLayout>
                    <CustomerAuthStep
                        onCompleted={() =>
                            !hasMailingAddressStep
                                ? setWorkflowStage('application-with-co-application')
                                : setWorkflowStage('application')
                        }
                        onBack={() => setWorkflowStage('plan-selection')}
                    />
                </DefaultLayout>
            );
            break;
        case 'second-look-application':
            stageContent = (
                <DefaultLayout>
                    <SelectSecondLookStep
                        skipSelectProvider={!canChangeProvider}
                        onPrequalifySelected={() =>
                            setWorkflowStage('second-look-applicant-information')
                        }
                    />
                </DefaultLayout>
            );
            break;
        case 'application':
            stageContent = <ApplicationWorkflow />;
            break;
        case 'application-with-co-application':
            stageContent = <ApplicationWithCoApplicationWorkflow />;
            break;
        case 'second-look-applicant-information':
            stageContent = <SecondLookApplicationWorkflow useCustomButtons={canChangeProvider} />;
            break;
        case 'second-look-submitting':
            stageContent = (
                <DefaultLayout>
                    <ErrorBoundary
                        actionCausingError="submitting second look application"
                        onRetry={() => setWorkflowStage('second-look-applicant-information')}
                    >
                        <SecondLookSubmitLoanWorkflow />
                    </ErrorBoundary>
                </DefaultLayout>
            );
            break;
        case 'second-look-decision':
            stageContent = (
                <DefaultLayout>
                    <ErrorBoundary actionCausingError="loading qualification decision">
                        <SecondLookDecisionWorkflow />
                    </ErrorBoundary>
                </DefaultLayout>
            );
            break;
        case 'submitting':
            stageContent = (
                <DefaultLayout>
                    <ErrorBoundary
                        actionCausingError="submitting a loan"
                        onErrorTracked={reportSubmitToDecisionError}
                        onRetry={() =>
                            !hasMailingAddressStep
                                ? setWorkflowStage('application-with-co-application')
                                : setWorkflowStage('application')
                        }
                    >
                        <SubmitLoan />
                    </ErrorBoundary>
                </DefaultLayout>
            );
            break;
        case 'decision':
            stageContent =
                decisionData.decision === SubmitApplicationDecision.Declined ||
                decisionData.decision === SubmitApplicationDecision.Error ? (
                    <FinalStepLayout>
                        <Declined />
                    </FinalStepLayout>
                ) : decisionData.decision === SubmitApplicationDecision.Review ? (
                    <FinalStepLayout>
                        <UnderReview />
                    </FinalStepLayout>
                ) : (
                    <DecisionWorkflow />
                );
            break;
        case 'accept-offer':
            stageContent = (
                <DefaultLayout>
                    <Approved />
                </DefaultLayout>
            );
            break;
        case 'update-offer':
            stageContent = (
                <DefaultLayout>
                    <UpdateOfferWorkflow />
                </DefaultLayout>
            );
            break;
        case 'reviewing': {
            const isWellsFargoProvider = setupData?.provider?.toLowerCase() === 'wellsfargo';
            stageContent = (
                <DefaultLayout>
                    <ErrorBoundary
                        actionCausingError={
                            isWellsFargoProvider
                                ? 'reviewing your pre-qualification'
                                : 'reviewing a loan'
                        }
                        onErrorTracked={reportSubmitToDecisionError}
                        onRetry={() => setWorkflowStage('application-with-co-application')}
                    >
                        {isWellsFargoProvider ? (
                            <WellsFargoReviewingWorkflow />
                        ) : (
                            <ReviewingWorkflow />
                        )}
                    </ErrorBoundary>
                </DefaultLayout>
            );
            break;
        }
        case 'signing':
            stageContent = signatureData.signedSuccessfully ? (
                <FinalStepLayout
                    doneButtonText={
                        setupData?.provider?.toLowerCase() === 'greensky'
                            ? 'View Estimates'
                            : 'Done'
                    }
                >
                    <LoanSignContainer />
                </FinalStepLayout>
            ) : (
                <DefaultLayout>
                    <LoanSignContainer />
                </DefaultLayout>
            );
            break;
        case 'reset':
            stageContent = <ResetWorkflow />;
            break;
        default:
            stageContent = null;
    }

    if (SUBMIT_TO_DECISION_STAGES.includes(workflowStage)) {
        return (
            <ApplicationSubmitToDecisionProvider
                context={{
                    provider: setupData?.provider,
                    experience: setupData?.role,
                    decision: decisionData?.decision,
                }}
            >
                {stageContent}
            </ApplicationSubmitToDecisionProvider>
        );
    }

    return stageContent;
};
